import React, { useCallback } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@material-ui/core";
import {
  Search as SearchIcon,
  ViewModule as GridViewIcon,
  ViewList as ListViewIcon,
  Add as AddIcon,
} from "@material-ui/icons";
import { makeStyles } from "@material-ui/styles";

const useStyles = makeStyles(theme => ({
  toolbarContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: "wrap",
    gap: theme.spacing(2),
  },
  searchField: {
    backgroundColor: "#ffffff",
    borderRadius: 8,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
    },
  },
  viewToggleContainer: {
    backgroundColor: "#f5f5f5",
    borderRadius: 8,
    padding: 4,
    display: "flex",
  },
  viewToggleButton: {
    minWidth: 40,
    padding: theme.spacing(1),
    borderRadius: 4,
  },
  activeViewButton: {
    backgroundColor: "#ffffff",
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
  },
  addLinkButton: {
    borderRadius: 4,
    backgroundColor: "#4285F4",
    color: "white",
    "&:hover": {
      backgroundColor: "#3b77db",
    },
    padding: theme.spacing(1, 3),
  },
  actionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
}));

/**
 * Enhanced search toolbar with view mode toggle and add link functionality
 */
const SearchToolbar = React.memo(
  ({
    searchQuery,
    setSearchQuery,
    viewMode,
    setViewMode,
    userAdmin,
    onAddClick,
  }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    // Memoized search handler to prevent unnecessary re-renders
    const handleSearchChange = useCallback(
      e => {
        setSearchQuery(e.target.value);
      },
      [setSearchQuery]
    );

    // Memoized view mode toggle handlers
    const handleGridView = useCallback(() => {
      setViewMode("grid");
    }, [setViewMode]);

    const handleListView = useCallback(() => {
      setViewMode("list");
    }, [setViewMode]);

    return (
      <Box
        className={classes.toolbarContainer}
        mb={4}
        aria-label="Search and view options toolbar"
      >
        <Box flexGrow={1} mr={2} style={{ maxWidth: isMobile ? "100%" : 300 }}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search links..."
            className={classes.searchField}
            value={searchQuery}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon aria-hidden="true" />
                </InputAdornment>
              ),
              "aria-label": "Search links",
            }}
            size="small"
          />
        </Box>

        <Box className={classes.actionsContainer}>
          <Box
            className={classes.viewToggleContainer}
            role="radiogroup"
            aria-label="View mode selection"
          >
            <IconButton
              className={`${classes.viewToggleButton} ${
                viewMode === "grid" ? classes.activeViewButton : ""
              }`}
              onClick={handleGridView}
              aria-pressed={viewMode === "grid"}
              aria-label="Grid view"
            >
              <GridViewIcon />
            </IconButton>
            <IconButton
              className={`${classes.viewToggleButton} ${
                viewMode === "list" ? classes.activeViewButton : ""
              }`}
              onClick={handleListView}
              aria-pressed={viewMode === "list"}
              aria-label="List view"
            >
              <ListViewIcon />
            </IconButton>
          </Box>
          {userAdmin && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={onAddClick}
              aria-label="Add new link"
            >
              Add Link
            </Button>
          )}
        </Box>
      </Box>
    );
  }
);

export default SearchToolbar;
